"use client";

import RemoveAllIcon from "@/app/assets/images/svg/rotate.svg";
import RemoveIcon from "@/app/assets/images/svg/xmark.svg";
import { useI18n } from "@/i18n/client";
import { FacetType, FacetValue } from "@/lib/searchspring/types";
import { getFacetIdentifier, getFacetParams } from "@/lib/searchspring/utils";
import { FC, useContext, useEffect, useState } from "react";
import { SearchSpringContext } from "../../SearchSpringContext/SearchSpringContextProvider";
import styles from "./ActiveFilters.module.scss";

export interface ActiveFiltersProps {
	filters: FacetType[] | undefined;
	clearAllFilters: () => void;
}

type AppliedFilterType = FacetValue & {
	field: string;
};

const ActiveFilters: FC<ActiveFiltersProps> = ({
	filters,
	clearAllFilters,
}) => {
	const { handleRemoveFacet, setFacetsToApply } =
		useContext(SearchSpringContext);
	const [activeFilters, setActiveFilters] = useState<AppliedFilterType[]>([]);

	const t = useI18n();

	useEffect(() => {
		if (!filters) return;

		const tempFilters = [];

		// Get an array of all the active filters
		for (const { field, values } of filters) {
			for (const value of values) {
				if (value.filtered) {
					tempFilters.push({
						field,
						...value,
					});
				}
			}
		}

		setActiveFilters(tempFilters);
	}, [filters]);

	const removeFilter = (item: AppliedFilterType) => {
		const facetId = getFacetIdentifier({
			field: item.field,
			value: item.value,
			high: item.high,
			low: item.low,
		});
		handleRemoveFacet(facetId);

		setActiveFilters((prevFilters) =>
			prevFilters.filter((filter) => getFacetIdentifier(filter) !== facetId),
		);
	};

	const handleClearAllFilters = () => {
		setFacetsToApply([]);
		clearAllFilters();
		setActiveFilters([]);
	};

	if (!activeFilters.length) return null;

	return (
		<div className={styles.container}>
			<h3 className={styles.title}>
				{t("search.selectedFilters")} ({activeFilters.length})
			</h3>
			<>
				<button
					type='button'
					className={styles.removeAll}
					onClick={handleClearAllFilters}
				>
					<RemoveAllIcon />
					{t("search.clearFilters")}
				</button>

				<div className={styles.filters}>
					{activeFilters.map((item) => {
						const key = getFacetIdentifier({
							field: item.field,
							value: item.value,
							high: item.high,
							low: item.low,
						});

						return (
							<button
								key={key}
								type='button'
								className={styles.removeFilter}
								onClick={() => removeFilter(item)}
							>
								{item.label} <RemoveIcon />
							</button>
						);
					})}
				</div>
			</>
		</div>
	);
};

export default ActiveFilters;
